import { useEffect, useMemo, useState } from "react";
import { createNote, getAllNotes, updateNote } from "../notes/NoteService";
import "./Journal.css";

const ENTRY_TYPES = [
  { value: "FOOD", label: "Food" },
  { value: "EXPENSE", label: "Expense" },
  { value: "WORK", label: "Work" },
  { value: "NOTE", label: "Note" },
];

function toDateKey(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function nowHHMM() {
  const d = new Date();
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

function parseEntries(content) {
  if (!content) return [];
  try {
    const parsed = JSON.parse(content);
    if (!Array.isArray(parsed?.entries)) return [];
    return parsed.entries;
  } catch {
    return [];
  }
}

function buildSummary(entries) {
  if (!entries.length) return "";
  const counts = entries.reduce(
    (acc, e) => {
      const kind = (e?.kind || "NOTE").toUpperCase();
      acc[kind] = (acc[kind] || 0) + 1;
      return acc;
    },
    {}
  );
  return `Food:${counts.FOOD || 0}, Expense:${counts.EXPENSE || 0}, Work:${counts.WORK || 0}, Note:${counts.NOTE || 0}`;
}

export default function Journal() {
  const todayKey = useMemo(() => toDateKey(new Date()), []);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [status, setStatus] = useState("");
  const [journalNote, setJournalNote] = useState(null);
  const [entries, setEntries] = useState([]);

  const [type, setType] = useState("NOTE");
  const [time, setTime] = useState(nowHHMM());
  const [text, setText] = useState("");
  const [amount, setAmount] = useState("");

  const hydrateTodayJournal = async () => {
    setLoading(true);
    setError("");
    try {
      const all = await getAllNotes();
      const todayJournal = (all || []).find(
        (n) =>
          n.noteType === "JOURNAL" &&
          n.createdDate === todayKey &&
          n.status !== "Deleted" &&
          n.status !== "DELETED"
      );

      if (todayJournal) {
        setJournalNote(todayJournal);
        setEntries(parseEntries(todayJournal.content));
      } else {
        const created = await createNote({
          title: `Journal ${todayKey}`,
          content: JSON.stringify({ entries: [] }),
          noteType: "JOURNAL",
          priority: 3,
          status: "ACTIVE",
          secured: false,
          noteSummary: "",
        });
        setJournalNote(created);
        setEntries([]);
      }
    } catch {
      setError("Unable to load journal right now.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    hydrateTodayJournal();
  }, []);

  useEffect(() => {
    if (!status) return undefined;
    const t = setTimeout(() => setStatus(""), 2200);
    return () => clearTimeout(t);
  }, [status]);

  const persistEntries = async (nextEntries) => {
    if (!journalNote?.id) return;
    setSaving(true);
    setError("");
    try {
      const updated = await updateNote(journalNote.id, {
        title: `Journal ${todayKey}`,
        content: JSON.stringify({ entries: nextEntries }),
        noteType: "JOURNAL",
        priority: 3,
        status: "ACTIVE",
        secured: false,
        noteSummary: buildSummary(nextEntries),
      });
      setJournalNote(updated);
      setEntries(nextEntries);
      setStatus("Saved");
    } catch {
      setError("Failed to save journal.");
    } finally {
      setSaving(false);
    }
  };

  const addEntry = async () => {
    const clean = text.trim();
    if (!clean || saving) return;
    const item = {
      id: `jr-${Date.now()}-${Math.random().toString(16).slice(2, 7)}`,
      kind: type,
      time: time || nowHHMM(),
      text: clean,
      amount: type === "EXPENSE" && amount ? Number(amount) : null,
    };
    const next = [...entries, item].sort((a, b) => (a.time > b.time ? 1 : -1));
    await persistEntries(next);
    setText("");
    setAmount("");
    setTime(nowHHMM());
  };

  const removeEntry = async (id) => {
    if (saving) return;
    const next = entries.filter((e) => e.id !== id);
    await persistEntries(next);
  };

  return (
    <div className="journal-wrap">
      <div className="journal-head">
        <h2>Daily Journal</h2>
        <div className="journal-date">{todayKey}</div>
      </div>

      {loading ? <p className="journal-muted">Loading journal...</p> : null}
      {error ? <p className="journal-error">{error}</p> : null}
      {status ? <p className="journal-success">{status}</p> : null}

      {!loading && (
        <>
          <div className="journal-form">
            <select value={type} onChange={(e) => setType(e.target.value)}>
              {ENTRY_TYPES.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>
            <input type="time" value={time} onChange={(e) => setTime(e.target.value)} />
            <input
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="What happened?"
            />
            {type === "EXPENSE" && (
              <input
                type="number"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="Amount"
              />
            )}
            <button type="button" onClick={addEntry} disabled={saving}>
              {saving ? "Saving..." : "Add Entry"}
            </button>
          </div>

          <div className="journal-list">
            {entries.length === 0 ? (
              <p className="journal-muted">No entries yet. Start logging your day.</p>
            ) : (
              entries.map((e) => (
                <div key={e.id} className={`journal-item journal-${(e.kind || "NOTE").toLowerCase()}`}>
                  <div className="journal-item-top">
                    <span className="journal-pill">{e.kind}</span>
                    <span className="journal-time">{e.time}</span>
                  </div>
                  <div className="journal-text">{e.text}</div>
                  {e.kind === "EXPENSE" && e.amount != null ? (
                    <div className="journal-amount">Cost: {e.amount}</div>
                  ) : null}
                  <button
                    type="button"
                    className="journal-remove"
                    onClick={() => removeEntry(e.id)}
                    disabled={saving}
                  >
                    Remove
                  </button>
                </div>
              ))
            )}
          </div>
        </>
      )}
    </div>
  );
}
